import { useCallback, useEffect, useState } from 'react';
import { adminLogout } from '../../lib/adminAuth';
// ボタン・入力欄・モーダル（pp-*）はパスポート画面の共通スタイルを使う
import './PassportPage.css';
import './AdminUsersPage.css';

import { tx } from '../../lib/adminI18n';
// 管理者アカウント  /admin/admin-users
//   一覧・追加・表示名変更・パスワード変更・有効/無効の切替（削除はしない）。
interface AdminUserRow {
  id: number; login_id: string; name: string; is_active: number;
  last_login_at: string | null; created_at: string | null;
}
type Dialog =
  | { kind: 'add' }
  | { kind: 'name'; user: AdminUserRow }
  | { kind: 'password'; user: AdminUserRow }
  | null;

const MIN_PW = 12;

function fmt(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso.endsWith('Z') ? iso : iso + 'Z');
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}

async function api(path: string, method = 'GET', body?: unknown) {
  const res = await fetch(`/api/admin/admin-users${path}`, {
    method,
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
    credentials: 'include',
  });
  const data = await res.json().catch(() => ({} as any));
  if (!res.ok) throw new Error(data?.error || tx('au.0', { p0: res.status }));
  return data;
}

export default function AdminUsersPage({ embedded = false }: { embedded?: boolean } = {}) {
  const [users, setUsers] = useState<AdminUserRow[]>([]);
  const [meId, setMeId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [dialog, setDialog] = useState<Dialog>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api('');
      setUsers(d.users || []);
      setMeId(d.me_id ?? null);
    } catch (e: any) {
      setMsg(e.message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  async function toggleActive(u: AdminUserRow) {
    const next = u.is_active ? 0 : 1;
    if (!next && !window.confirm(tx('au.1', { p0: u.name }))) return;
    try {
      await api(`/${u.id}/active`, 'PATCH', { is_active: next });
      setMsg(next ? tx('au.2', { p0: u.name }) : tx('au.3', { p0: u.name }));
      await load();
    } catch (e: any) { setMsg(e.message); }
  }

  return (
    <div className={embedded ? 'au-page au-embedded' : 'admin-page au-page'}>
      <div className="au-head">
        {embedded ? <h2 className="set-section-title" style={{ margin: 0, border: 'none' }}>{tx('au.4')}</h2>
                  : <h1 className="admin-page-title">{tx('au.4')}</h1>}
        <button className="pp-btn" onClick={() => setDialog({ kind: 'add' })}>{tx('au.5')}</button>
      </div>
      {msg && <p className="au-msg" role="status">{msg}</p>}

      {loading ? <p>{tx('au.6')}</p> : (
        <table className="au-table">
          <thead>
            <tr><th>{tx('au.7')}</th><th>{tx('au.8')}</th><th>{tx('au.9')}</th><th>{tx('au.10')}</th><th>{tx('au.11')}</th></tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className={u.is_active ? '' : 'au-inactive'}>
                <td>{u.name}{u.id === meId && <span className="au-me">{tx('au.12')}</span>}</td>
                <td><code>{u.login_id}</code></td>
                <td>{u.is_active ? <span className="au-badge au-on">{tx('au.13')}</span> : <span className="au-badge au-off">{tx('au.14')}</span>}</td>
                <td>{fmt(u.last_login_at)}</td>
                <td className="au-actions">
                  <button className="pp-btn-sm" onClick={() => setDialog({ kind: 'name', user: u })}>{tx('au.15')}</button>
                  <button className="pp-btn-sm" onClick={() => setDialog({ kind: 'password', user: u })}>{tx('au.16')}</button>
                  {u.id !== meId && (
                    <button className="pp-btn-ghost" onClick={() => toggleActive(u)}>{u.is_active ? tx('au.17') : tx('au.18')}</button>
                  )}
                </td>
              </tr>
            ))}
            {users.length === 0 && <tr><td colSpan={5}>{tx('au.19')}</td></tr>}
          </tbody>
        </table>
      )}
      <p className="au-note">{tx('au.20')}</p>

      {dialog && (
        <div className="pp-detail-overlay" onClick={() => setDialog(null)}>
          <div className="pp-detail pp-detail-modal au-modal" onClick={(e) => e.stopPropagation()}>
            {dialog.kind === 'add' && <AddForm onDone={async (m) => { setDialog(null); setMsg(m); await load(); }} onCancel={() => setDialog(null)} />}
            {dialog.kind === 'name' && <NameForm user={dialog.user} onDone={async (m) => { setDialog(null); setMsg(m); await load(); }} onCancel={() => setDialog(null)} />}
            {dialog.kind === 'password' && (
              <PasswordForm user={dialog.user} isSelf={dialog.user.id === meId}
                onDone={async (m, relogin) => {
                  setDialog(null);
                  if (relogin) { window.alert(tx('au.21')); await adminLogout(); return; }
                  setMsg(m); await load();
                }}
                onCancel={() => setDialog(null)} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function FormShell({ title, error, busy, onCancel, submitLabel, onSubmit, children }: {
  title: string; error: string; busy: boolean; onCancel: () => void; submitLabel: string;
  onSubmit: (e: React.FormEvent) => void; children: React.ReactNode;
}) {
  return (
    <form className="au-form" onSubmit={onSubmit}>
      <div className="au-form-head">
        <strong>{title}</strong>
        <button type="button" className="pp-btn-ghost" onClick={onCancel} aria-label={tx('au.22')}>×</button>
      </div>
      {children}
      {error && <div className="au-error" role="alert">⚠ {error}</div>}
      <div className="au-form-foot">
        <button type="button" className="pp-btn-ghost" onClick={onCancel} disabled={busy}>{tx('au.23')}</button>
        <button type="submit" className="pp-btn" disabled={busy}>{busy ? tx('au.24') : submitLabel}</button>
      </div>
    </form>
  );
}

function AddForm({ onDone, onCancel }: { onDone: (m: string) => void; onCancel: () => void }) {
  const [loginId, setLoginId] = useState('');
  const [name, setName] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  async function submit(e: React.FormEvent) {
    e.preventDefault(); setError('');
    if (pw.length < MIN_PW) return setError(tx('au.25', { p0: MIN_PW }));
    if (pw !== pw2) return setError(tx('au.26'));
    setBusy(true);
    try { await api('', 'POST', { login_id: loginId, name, password: pw }); onDone(tx('au.27', { p0: name })); }
    catch (e: any) { setError(e.message); } finally { setBusy(false); }
  }
  return (
    <FormShell title={tx('au.28')} error={error} busy={busy} onCancel={onCancel} submitLabel={tx('au.29')} onSubmit={submit}>
      <label className="pp-label">{tx('au.7')}<input className="pp-input" value={name} onChange={(e) => setName(e.target.value)} autoFocus /></label>
      <label className="pp-label">{tx('au.30')}
        <input className="pp-input" value={loginId} onChange={(e) => setLoginId(e.target.value)} autoComplete="off" autoCapitalize="none" spellCheck={false} />
      </label>
      <label className="pp-label">{tx('au.31')}{MIN_PW}{tx('au.32')}<input className="pp-input" type="password" value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="new-password" /></label>
      <label className="pp-label">{tx('au.33')}<input className="pp-input" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} autoComplete="new-password" /></label>
    </FormShell>
  );
}

function NameForm({ user, onDone, onCancel }: { user: AdminUserRow; onDone: (m: string) => void; onCancel: () => void }) {
  const [name, setName] = useState(user.name);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  async function submit(e: React.FormEvent) {
    e.preventDefault(); setError(''); setBusy(true);
    try { await api(`/${user.id}`, 'PATCH', { name }); onDone(tx('au.34')); }
    catch (e: any) { setError(e.message); } finally { setBusy(false); }
  }
  return (
    <FormShell title={tx('au.35', { p0: user.login_id })} error={error} busy={busy} onCancel={onCancel} submitLabel={tx('au.36')} onSubmit={submit}>
      <label className="pp-label">{tx('au.7')}<input className="pp-input" value={name} onChange={(e) => setName(e.target.value)} autoFocus /></label>
    </FormShell>
  );
}

function PasswordForm({ user, isSelf, onDone, onCancel }: {
  user: AdminUserRow; isSelf: boolean; onDone: (m: string, relogin: boolean) => void; onCancel: () => void;
}) {
  const [cur, setCur] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  async function submit(e: React.FormEvent) {
    e.preventDefault(); setError('');
    if (pw.length < MIN_PW) return setError(tx('au.25', { p0: MIN_PW }));
    if (pw !== pw2) return setError(tx('au.26'));
    setBusy(true);
    try {
      const d = await api(`/${user.id}/password`, 'POST', { password: pw, current_password: isSelf ? cur : undefined });
      onDone(tx('au.37', { p0: user.name }), !!d.relogin);
    } catch (e: any) { setError(e.message); } finally { setBusy(false); }
  }
  return (
    <FormShell title={tx('au.38', { p0: user.name })} error={error} busy={busy} onCancel={onCancel} submitLabel={tx('au.39')} onSubmit={submit}>
      {isSelf && (
        <label className="pp-label">{tx('au.40')}<input className="pp-input" type="password" value={cur} onChange={(e) => setCur(e.target.value)} autoComplete="current-password" autoFocus /></label>
      )}
      <label className="pp-label">{tx('au.41')}{MIN_PW}{tx('au.32')}<input className="pp-input" type="password" value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="new-password" autoFocus={!isSelf} /></label>
      <label className="pp-label">{tx('au.42')}<input className="pp-input" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} autoComplete="new-password" /></label>
      <p className="au-note">{isSelf ? tx('au.43') : tx('au.44')}</p>
    </FormShell>
  );
}
